
import React, { useState } from 'react';
import { Upload, Image, Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { isAnimatedGif, convertPsdToImage } from '@/utils/banner/templates/imageProcessing';
import { useToast } from '@/hooks/use-toast';

interface AnimatedImageUploaderProps {
  onImageUploaded: (file: File, imageUrl: string, isAnimated: boolean) => void;
}

const AnimatedImageUploader: React.FC<AnimatedImageUploaderProps> = ({ onImageUploaded }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isAnimated, setIsAnimated] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const { toast } = useToast();
  
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsLoading(true);
    setFileName(file.name);
    
    try {
      // Check if it's a supported file type
      if (file.type.includes('image/gif')) {
        // Check if the GIF is animated
        const animated = await isAnimatedGif(file);
        setIsAnimated(animated);
        
        // Create preview URL
        const imageUrl = URL.createObjectURL(file);
        setPreviewUrl(imageUrl);
        onImageUploaded(file, imageUrl, animated);
        
        toast({
          title: "GIF uploaded successfully",
          description: animated ? "Animated GIF detected" : "Static GIF detected"
        });
      } else if (file.type.includes('image/vnd.adobe.photoshop') || file.name.toLowerCase().endsWith('.psd')) {
        // Handle PSD files
        toast({
          title: "Processing PSD file",
          description: "Converting PSD to image format..."
        });
        
        // In a real implementation, this would convert the PSD to a viewable format
        const imageUrl = await convertPsdToImage(file);
        setPreviewUrl(imageUrl);
        setIsAnimated(false);
        onImageUploaded(file, imageUrl, false);
        
        toast({
          title: "PSD processed",
          description: "PSD file converted successfully"
        });
      } else if (file.type.includes('image/')) {
        // Handle other image types
        const imageUrl = URL.createObjectURL(file);
        setPreviewUrl(imageUrl);
        setIsAnimated(false);
        onImageUploaded(file, imageUrl, false);
        
        toast({
          title: "Image uploaded",
          description: "Image uploaded successfully"
        });
      } else {
        // Unsupported file type
        toast({
          title: "Unsupported file type",
          description: "Please upload a GIF, PSD, JPG, or PNG file",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error processing file:', error);
      toast({
        title: "Error processing file",
        description: "There was a problem processing your file",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleAnimation = () => {
    if (!previewUrl || !isAnimated) return;
    
    const img = document.getElementById('preview-gif') as HTMLImageElement;
    if (img) {
      if (isPaused) {
        // Resume animation
        img.src = img.src;
        setIsPaused(false);
      } else {
        // Pause animation by replacing with a data URL of the current frame
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, img.width, img.height);
          const dataUrl = canvas.toDataURL('image/png');
          img.src = dataUrl;
          setIsPaused(true);
        }
      }
    }
  };
  
  return (
    <div className="space-y-4">
      <div>
        <Label>Banner Image</Label>
        <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center mt-1">
          <input
            type="file"
            id="animated-image-upload"
            className="hidden"
            accept="image/gif,image/jpeg,image/png,image/vnd.adobe.photoshop,.psd"
            onChange={handleFileUpload}
          />
          <label htmlFor="animated-image-upload" className="cursor-pointer">
            <div className="flex flex-col items-center justify-center py-4">
              <Upload className="h-10 w-10 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 font-medium">
                Click to upload an image
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supports GIF (animated), PSD, JPG, PNG
              </p>
            </div>
          </label>
        </div>
      </div>
      
      {isLoading && (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
      
      {previewUrl && !isLoading && (
        <div className="space-y-2">
          <div className="bg-gray-100 rounded-md p-2 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Image className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700 truncate max-w-[200px]">{fileName}</span>
            </div>
            {isAnimated && (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleAnimation}
                className="h-8 w-8 p-0"
              >
                {isPaused ? (
                  <Play className="h-4 w-4" />
                ) : (
                  <Pause className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
          
          <div className="rounded-md overflow-hidden border border-gray-200">
            <img
              id="preview-gif"
              src={previewUrl}
              alt="Preview"
              className="w-full h-auto object-contain"
            />
          </div>
          
          {isAnimated && (
            <p className="text-sm text-blue-600 flex items-center">
              <span>Animated GIF detected</span>
              {isPaused ? (
                <span className="ml-2 px-2 py-0.5 bg-gray-200 rounded text-xs">Paused</span>
              ) : (
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">Playing</span>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AnimatedImageUploader;
